import { Link, usePage } from "@inertiajs/react"
import { ChevronDown } from "lucide-react"
import type { PropsWithChildren } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import {
  settingsAccountPath,
  settingsBillingPath,
  settingsBusinessProfilePath,
  settingsIdentityPath,
  settingsIntegrationsPath,
  settingsKnowledgeItemsPath,
  settingsManagePath,
  settingsNotificationsPath,
  settingsPasswordPath,
  settingsPhonePath,
  settingsProfilePath,
  settingsUsersPath,
} from "@/routes"
import type { NavItem } from "@/types"

// A group heading. Names the scope of the links beneath it rather than just
// dividing them, matching the app sidebar's group labels.
type NavEntry = NavItem | { heading: string }

function buildSidebarNavItems(
  demoMode: boolean,
  imperativeManaged: boolean,
): NavEntry[] {
  // Workspace-scoped settings. Knowledge base sits next to Business profile —
  // both are "what the agent knows about the business."
  const workspace: NavItem[] = [
    { title: "General", href: settingsAccountPath(), icon: null },
    {
      title: "Business profile",
      href: settingsBusinessProfilePath(),
      icon: null,
    },
    {
      title: "Knowledge base",
      href: settingsKnowledgeItemsPath(),
      icon: null,
    },
    // IMP-456: the assistant's account-level identity — voice, communication
    // style, AI disclosure — inherited by every job.
    { title: "Identity", href: settingsIdentityPath(), icon: null },
    { title: "Members", href: settingsUsersPath(), icon: null },
    { title: "Integrations", href: settingsIntegrationsPath(), icon: null },
    // IMP-387: always shown. The page itself renders an empty state until an
    // agent that needs a number is activated.
    { title: "Phone", href: settingsPhonePath(), icon: null },
  ]

  // IMP-231: client subscription/billing is only relevant for
  // imperative-managed partners; partner-managed accounts are billed by their
  // provider. The controller also hard-gates (redirects) for them.
  if (imperativeManaged) {
    workspace.push({
      title: "Billing",
      href: settingsBillingPath(),
      icon: null,
    })
  }

  // Personal, per-user settings.
  const personal: NavItem[] = [
    { title: "Profile", href: settingsProfilePath(), icon: null },
    { title: "Notifications", href: settingsNotificationsPath(), icon: null },
    { title: "Password", href: settingsPasswordPath(), icon: null },
  ]

  // IMP-228: demo-only entry point for self-serve reset actions. The
  // controller hard-gates on @account.demo_mode? — hiding the link
  // client-side is just to keep the nav clean for paying accounts. It resets
  // the account, so it belongs under Account rather than Personal.
  if (demoMode) {
    workspace.push({ title: "Manage", href: settingsManagePath(), icon: null })
  }

  return [
    { heading: "Account" },
    ...workspace,
    { heading: "Personal" },
    ...personal,
  ]
}

export default function SettingsLayout({
  children,
  wide,
}: PropsWithChildren<{ wide?: boolean }>) {
  const page = usePage<{
    account?: { demo_mode?: boolean }
    partner?: { billing_type?: string }
  }>()
  const navItems = buildSidebarNavItems(
    page.props.account?.demo_mode ?? false,
    page.props.partner?.billing_type === "imperative_managed",
  )
  const url = page.url
  // Matches the aside's exact-match highlighting. A settings page that isn't in
  // the nav (Appearance) falls back to "Settings" rather than naming the wrong
  // page.
  const currentTitle =
    navItems.find(
      (item): item is NavItem => !("heading" in item) && item.href === url,
    )?.title ?? "Settings"

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      {/* IMP-518 (Compass): same scale as PageHeading — structure comes from
          spacing and ink, not display type. */}
      <h1 className="mb-6 text-lg/7 font-semibold lg:mb-8">Settings</h1>
      {/* Below `lg` the full nav would push the actual settings more than a
          screen down, so it collapses to a picker showing the current page.
          `lg` is where the two-column layout starts and the aside takes over. */}
      <div className="mb-6 lg:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              {currentTitle}
              <ChevronDown className="size-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          {/* Menu of links, not a Select: a Select renders a hidden native
              <select>, and the browser restoring its form state on load fires
              onValueChange — which navigated the user to a random settings
              page on every visit. */}
          <DropdownMenuContent
            align="start"
            className="w-(--radix-dropdown-menu-trigger-width)"
          >
            {navItems.map((item, index) =>
              "heading" in item ? (
                <DropdownMenuLabel
                  key={`heading-${item.heading}`}
                  className="text-muted-foreground text-xs font-medium"
                >
                  {item.heading}
                </DropdownMenuLabel>
              ) : (
                <DropdownMenuItem key={`${item.href}-${index}`} asChild>
                  <Link href={item.href}>{item.title}</Link>
                </DropdownMenuItem>
              ),
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col space-y-8 lg:flex-row lg:space-y-0 lg:space-x-12">
        <aside className="hidden w-full max-w-xl lg:block lg:w-48">
          <nav className="flex flex-col space-y-1 space-x-0">
            {navItems.map((item, index) =>
              "heading" in item ? (
                <p
                  key={`heading-${item.heading}`}
                  className="text-muted-foreground mt-4 flex h-8 shrink-0 items-center px-2 text-xs font-medium first:mt-0"
                >
                  {item.heading}
                </p>
              ) : (
                <Button
                  key={`${item.href}-${index}`}
                  size="sm"
                  variant="ghost"
                  asChild
                  className={cn("w-full justify-start", {
                    "bg-muted": url === item.href,
                  })}
                >
                  <Link href={item.href}>
                    {item.icon && <item.icon className="h-4 w-4" />}
                    {item.title}
                  </Link>
                </Button>
              ),
            )}
          </nav>
        </aside>

        <div className={cn("flex-1", !wide && "md:max-w-2xl")}>
          <section className={cn("space-y-12", !wide && "max-w-xl")}>
            {children}
          </section>
        </div>
      </div>
    </div>
  )
}
